import React from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import type { DavisBaconScheduleDetails, DavisBaconScheduleReviewState, DavisBaconScheduleStatus, DavisBaconScheduleSummary } from '../api/constructionPriceEstimatorFunctions';
import WageDeterminationResultCard, { type WageDeterminationResultCardData } from '../components/WageDeterminationResultCard';
import { ExternalField, ExternalTextarea } from './ConstructionPriceContractDetails';

const davisBaconScheduleStatusLabel = (status: DavisBaconScheduleStatus, reviewState: DavisBaconScheduleReviewState): string => {
  if (status === 'reference_found') return 'Wage Determination found';
  if (status === 'rejected' || reviewState === 'rejected') return 'Rejected';
  if (status === 'review_required' || reviewState === 'review_required') return 'Review required';
  return reviewState === 'validated' ? 'Validated' : 'Approved';
};

/** Adapt the safe Stage-623 summary to the shared Wage Determination card. */
const wageDeterminationCardData = (
  schedule: DavisBaconScheduleSummary,
): WageDeterminationResultCardData => ({
  wd_number: `${schedule.determination_id}/${schedule.revision}`,
  construction_type: schedule.construction_type,
  geographic_scope: `${schedule.county}, ${schedule.state}`,
  modification_date: schedule.effective_date ?? schedule.publication_date ?? schedule.reference_date,
  modification_number: schedule.revision,
  publication_date: schedule.publication_date,
  effective_date: schedule.effective_date,
  reference_date: schedule.reference_date,
  status: davisBaconScheduleStatusLabel(schedule.status, schedule.review_state),
  source_name: schedule.source_name,
  determination_url: schedule.determination_url,
});

/**
 * The schedule belongs with the labor evidence it supports.  This component is
 * only mounted inside the ROM table's collapsed evidence row after a result is
 * available, so wage classifications never become a second results panel.
 */
export const WageDeterminationEvidence: React.FC<{
  schedule: DavisBaconScheduleSummary | null;
  details: DavisBaconScheduleDetails | null;
  loading: boolean;
  unavailableReason?: string;
  review: React.ReactNode;
}> = ({ schedule, details, loading, unavailableReason, review }) => (
  <Box aria-label="Wage determination evidence">
    {schedule && <Box sx={{ mb: 2 }} aria-label="Wage determination summary">
      <WageDeterminationResultCard
        determination={wageDeterminationCardData(schedule)}
        cardId={`price-estimator-${schedule.determination_id}-${schedule.revision}`}
      />
    </Box>}
    {loading && <Stack role="status" aria-label="Loading wage determination details" direction="row" spacing={1} alignItems="center">
      <CircularProgress size={20} /><Typography>Loading wage determination details…</Typography>
    </Stack>}
    {details ? <Box aria-label="Wage determination details">
      <Typography fontWeight={700} sx={{ mb: 1 }}>Wage determination classifications</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Wage determination rates">
          <TableHead><TableRow><TableCell>Classification</TableCell><TableCell>Group</TableCell><TableCell>Base wage</TableCell><TableCell>Fringe</TableCell><TableCell>Total hourly</TableCell><TableCell>Unit</TableCell><TableCell>Footnotes</TableCell></TableRow></TableHead>
          <TableBody>{details.rows.map((row, index) => <TableRow key={`${row.classification}-${index}`}><TableCell>{row.classification}</TableCell><TableCell>{row.group ?? '—'}</TableCell><TableCell>${row.base_wage}</TableCell><TableCell>${row.fringe}</TableCell><TableCell>${row.total_hourly}</TableCell><TableCell>{row.unit}</TableCell><TableCell>{row.footnote_identifiers?.join(', ') || '—'}</TableCell></TableRow>)}</TableBody>
        </Table>
      </TableContainer>
      {review}
    </Box> : !loading && unavailableReason && <Typography>{unavailableReason}</Typography>}
  </Box>
);

export const WageDeterminationReview: React.FC<{
  reason: string;
  onReasonChange: (reason: string) => void;
  submitting: boolean;
  onDecision: (decision: 'approve' | 'reject') => void;
}> = ({ reason, onReasonChange, submitting, onDecision }) => (
  <Box sx={{ mt: 2 }} aria-label="Wage determination review">
    <ExternalField id="wage-determination-review-reason" label="Review rationale (required)">
      <ExternalTextarea id="wage-determination-review-reason" aria-label="Review rationale (required)"
        rows={3} value={reason} disabled={submitting}
        onChange={(event) => onReasonChange(event.target.value)} />
    </ExternalField>
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <Button variant="contained" disabled={!reason.trim() || submitting}
        onClick={() => onDecision('approve')}>
        {submitting ? 'Saving review…' : 'Approve determination'}
      </Button>
      <Button variant="outlined" color="error" disabled={!reason.trim() || submitting}
        onClick={() => onDecision('reject')}>Reject determination</Button>
    </Stack>
  </Box>
);
